import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import { SEO } from '../../../utils/seo';

export default function ConsultancyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Security Consultancy",
    "provider": {
      "@type": "LocalBusiness",
      "name": "LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "North Wales",
        "addressRegion": "Wales",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "North Wales"
    },
    "description": "Strategic security consultancy providing expert advice on security planning, system design, and implementation strategies tailored to your specific requirements.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Consultancy Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Security Planning",
            "description": "Comprehensive security planning services developing strategies aligned with business objectives"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "System Design",
            "description": "Expert system design services creating optimal security solutions"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Project Management",
            "description": "Professional project management ensuring security projects are delivered on time and within budget"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Security Consultancy North Wales - Expert Planning Design - LEWIS SECURITY"
        description="Strategic security consultancy providing expert advice on security planning, system design, and implementation strategies tailored to your specific requirements. Professional security consultants in North Wales."
        keywords="security consultancy North Wales, security planning, system design, security consultant, project management, security strategy"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900">
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-settings-line text-5xl text-white"></i>
              </div>
              <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-poppins">Consultancy Services</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto font-inter">
                Strategic security consultancy providing expert advice on security planning, system design, and implementation strategies tailored to your specific requirements
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6 font-poppins">Expert Security Guidance</h2>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Making the right security decisions requires expertise and experience. LEWIS SECURITY's consultancy services provide strategic guidance to help you design, implement, and manage security solutions that truly meet your needs and deliver value for your investment.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed font-inter">
                  Our consultants bring years of industry experience and deep knowledge of security technologies, regulations, and best practices. We work closely with you to understand your unique challenges and develop practical, cost-effective solutions that enhance security while supporting your business operations.
                </p>
              </div>
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/16b5563f38207541de8138eb37c247d0.jpg"
                  alt="Consultancy Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Consultancy Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-map-pin-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Security Planning</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Comprehensive security planning services developing strategies aligned with your business objectives. We create detailed security plans covering physical security, electronic systems, and operational procedures.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Strategic security assessments</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Security policy development</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Budget planning and ROI analysis</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Long-term security roadmaps</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-pencil-ruler-2-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">System Design</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Expert system design services creating optimal security solutions for your premises. We design integrated systems that maximize protection while remaining user-friendly and cost-effective.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Site surveys and analysis</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Technology selection guidance</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>System integration planning</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Detailed technical specifications</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-task-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Project Management</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Professional project management ensuring security projects are delivered on time, within budget, and to specification. We coordinate all aspects from planning through to commissioning.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Project planning and scheduling</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Contractor coordination</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Quality assurance and testing</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Handover and documentation</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-graduation-cap-line text-3xl text-red-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-poppins">Training & Development</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Comprehensive training programs ensuring your team can effectively use and manage security systems. We provide tailored training covering system operation, emergency procedures, and best practices.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>System operation training</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Emergency response procedures</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Security awareness programs</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-check-line text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Ongoing support and refresher courses</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Consultancy Approach */}
            <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-2xl p-12 mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Our Consultancy Approach</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 font-poppins">Understanding Your Needs</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    We begin every consultancy engagement by thoroughly understanding your business, operations, and specific security challenges. This ensures our recommendations are practical and aligned with your objectives.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Detailed needs analysis</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Stakeholder consultation</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Current system evaluation</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 font-poppins">Delivering Value</h3>
                  <p className="text-gray-600 mb-4 font-inter">
                    Our consultancy services focus on delivering tangible value through practical recommendations, clear documentation, and ongoing support. We ensure you have everything needed to make informed decisions.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Clear, actionable recommendations</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Comprehensive documentation</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700 font-inter">
                      <i className="ri-checkbox-circle-fill text-red-600 mr-2 text-lg mt-0.5"></i>
                      <span>Implementation support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Industries We Serve */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center font-poppins">Industries We Serve</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-building-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Commercial</h3>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-store-2-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Retail</h3>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-hospital-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Healthcare</h3>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-school-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Education</h3>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-hotel-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Hospitality</h3>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-factory-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Industrial</h3>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-government-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Public Sector</h3>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-md">
                  <i className="ri-home-4-line text-4xl text-red-600 mb-3"></i>
                  <h3 className="text-sm font-bold text-gray-900 font-poppins">Residential</h3>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="text-center bg-gray-900 rounded-2xl p-12">
              <h2 className="text-3xl font-bold text-white mb-4 font-poppins">Get Expert Security Advice</h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto font-inter">
                Make informed security decisions with expert consultancy. Contact us today to discuss your security challenges and discover how we can help
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/#contact" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Book Consultation
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
                <a 
                  href="tel:07834561015" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 07834 561015
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
